import React, { useState, useEffect, useRef, createContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import { getFilterDataByFilterType } from './filterFactory.js';
import { getNumbersOfAppliedFilters } from './filterHelpers.js';
import { useUser } from '../../../contexts/userContext.js';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';

const filtersState = {
  tagFilterNames: [],
  settings: [],
  appliedFilters: [],
  innerAppliedFilters: [],
  keyword: '',
  refresh: {},
  numOfAppliedFilters: 0,
  params: {},
  openFilters: false,
};

const FiltersContext = createContext(filtersState);

const FilterContextProvider = ({children}) => {
  const [tagFilterNames, setTagFilterNames] = useState([]);
  const [openFilters, setOpenFilters] = useState(false);
  const [settings, setSettings] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [innerAppliedFilters, setInnerAppliedFilters] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [refresh, setRefresh] = useState({});
  const [numOfAppliedFilters, setNumOfAppliedFilters] = useState(0);
  const [params, setParams] = useState({});
  const [isLinked, setIsLinked] = useState(false);
  const filtersWereOpen = useRef(false);
  const { pathname } = useLocation();
  const [currentFiltersLocation, setCurrentFiltersLocation] = useState(pathname);
  const { organizationBudgets, user, orgMatchesUrl } = useUser();
  const { nameConventions } = useNameConventions();

  useEffect(() => {
    if (!user?.activeBudget?.id) return;
    if (!orgMatchesUrl) return;
    let isCurrent = true;
    const fetchData = async () => {
      let filtersDataPromises = [];
      for (const tagFilterName of tagFilterNames) {
        filtersDataPromises.push(getFilterDataByFilterType(tagFilterName, params[tagFilterName], innerAppliedFilters, nameConventions, user?.activeBudget, organizationBudgets));
      }

      const responses = await Promise.all(filtersDataPromises);
      if (isCurrent) setSettings(responses);
    };

    fetchData();
    return () => {
      isCurrent = false;
    };
  }, [tagFilterNames, params, isLinked, refresh]);

  useEffect(() => {
    if (filtersWereOpen.current && !openFilters) {
      setInnerAppliedFilters(current => [
        ...current.filter(filter => !tagFilterNames.includes(filter.type)),
        ...appliedFilters.filter(filter => tagFilterNames.includes(filter.type)),
      ]);
    }
    filtersWereOpen.current = openFilters;
  }, [openFilters, tagFilterNames, appliedFilters]);

  const resetFilters = () => {
    // avoid changing reference when already empty to prevent unnecessary re-renders/effects
    setAppliedFilters(prev => (Array.isArray(prev) && prev.length === 0 ? prev : []));
    setInnerAppliedFilters([]);
    setNumOfAppliedFilters(0);
    setKeyword('');
    setOpenFilters(false);
    setRefresh({});
  };

  // reset during render (not in an effect) so a newly mounted page never reads the previous page's filters
  if (currentFiltersLocation !== pathname) {
    setCurrentFiltersLocation(pathname);
    resetFilters();
    setTagFilterNames([]);
    setSettings([]);
    setParams({});
    setIsLinked(false);
  }

  const applyOnlyAvailableFilters = (filterTypes) => {
    const filteredFilters = innerAppliedFilters.filter(innerFilter => filterTypes.includes(innerFilter.type));
    // create a deep copy to avoid sharing references; only update state if content actually changed
    const newApplied = JSON.parse(JSON.stringify(filteredFilters));
    setAppliedFilters(prev => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(newApplied)) return prev;
      } catch (e) {
        // on any error, fall back to updating
      }
      return newApplied;
    });
    setNumOfAppliedFilters(getNumbersOfAppliedFilters(newApplied));
  };

  const handleApplyFilters = () => {  
    applyOnlyAvailableFilters(tagFilterNames);
    handleCloseDrawer();
  };

  const clearAllFilters = () => {
    // RX-3738 - clear only filters we see on this tab, preserve rest
    setRefresh({});
    setInnerAppliedFilters(innerAppliedFilters.filter(innerFilter => !tagFilterNames.includes(innerFilter.type)));
  };

  const handleCloseDrawer = (manualClose) => {
    // 'manualClose' is true when the user closes the drawer via the X button or clicking outside the drawer.
    // In that case, we want to preserve the originally applied filters.
    if (manualClose) {
      setAppliedFilters(appliedFilters);
    }

    // the MAIN switch to close the drawer, whether manual or programmatic
    setOpenFilters(false);
  };

  return (
    <FiltersContext.Provider value={{
      tagFilterNames,
      setTagFilterNames,
      settings,
      appliedFilters,
      innerAppliedFilters,
      setInnerAppliedFilters,
      keyword,
      setKeyword,
      refresh,
      setRefresh,
      numOfAppliedFilters,
      setNumOfAppliedFilters,
      params,
      setParams,
      handleApplyFilters,
      applyOnlyAvailableFilters,
      setOpenFilters,
      openFilters,
      clearAllFilters,
      resetFilters,
      isLinked,
      setIsLinked,
      currentFiltersLocation,
      handleCloseDrawer,
    }}>
      {children}
    </FiltersContext.Provider>
  );
};

FilterContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FilterContextProvider;
export { FiltersContext };
